import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  listOfflineQueue,
  listOfflineZones,
  retryOfflineItem,
  synchronizeOfflineQueue,
} from "./offline-db";

export interface OfflineState {
  online: boolean;
  pending: number;
  failed: number;
  conflicts: number;
  zones: number;
  syncing: boolean;
}
const initial: OfflineState = {
  online: typeof navigator === "undefined" ? true : navigator.onLine,
  pending: 0,
  failed: 0,
  conflicts: 0,
  zones: 0,
  syncing: false,
};
let state = initial;
const listeners = new Set<(value: OfflineState) => void>();
function publish(next: OfflineState) {
  state = next;
  for (const listener of listeners) listener(next);
}
async function connectionAvailable() {
  if (!navigator.onLine) return false;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 3_000);
  try {
    const response = await fetch("/api/health", {
      cache: "no-store",
      credentials: "same-origin",
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timeout);
  }
}
async function refresh(
  syncing = state.syncing,
  online = state.online && navigator.onLine,
) {
  const [queue, zones] = await Promise.all([
    listOfflineQueue(),
    listOfflineZones(),
  ]);
  publish({
    online,
    pending: queue.filter(
      (item) => item.status === "pending" || item.status === "syncing",
    ).length,
    failed: queue.filter((item) => item.status === "failed").length,
    conflicts: queue.filter((item) => item.status === "conflict").length,
    zones: zones.length,
    syncing,
  });
}
export async function flushOfflineQueue(connectionConfirmed = false) {
  if (state.syncing) return;
  const online = connectionConfirmed || (await connectionAvailable());
  if (!online) {
    await refresh(false, false);
    return;
  }
  await refresh(true, true);
  try {
    await synchronizeOfflineQueue(async (input, photo) => {
      const record = await api.createNeed(input);
      if (photo)
        await api.uploadEvidence(photo, {
          needId: record.id,
          evidenceType: "operational",
        });
    });
  } finally {
    await refresh(false, await connectionAvailable());
  }
}
export function useOfflineState() {
  const [value, setValue] = useState(state);
  useEffect(() => {
    listeners.add(setValue);
    connectionAvailable()
      .then((online) => refresh(false, online))
      .catch(() => undefined);
    return () => {
      listeners.delete(setValue);
    };
  }, []);
  return value;
}
export function useOfflineQueue() {
  const [items, setItems] = useState<
    Awaited<ReturnType<typeof listOfflineQueue>>
  >([]);
  const load = useCallback(
    () =>
      listOfflineQueue()
        .then(setItems)
        .catch(() => setItems([])),
    [],
  );
  useEffect(() => {
    load();
    window.addEventListener("siscom-offline-change", load);
    return () => window.removeEventListener("siscom-offline-change", load);
  }, [load]);
  return {
    items,
    retry: async (id: string) => {
      await retryOfflineItem(id);
      await flushOfflineQueue();
      await load();
    },
    refresh: load,
  };
}
export function OfflineSyncProvider() {
  const client = useQueryClient();
  useEffect(() => {
    const online = () => {
      connectionAvailable()
        .then(async (connected) => {
          await refresh(false, connected);
          if (connected) await flushOfflineQueue(true);
        })
        .then(() => {
          client.invalidateQueries({ queryKey: ["needs"] });
          client.invalidateQueries({ queryKey: ["indicators"] });
          client.invalidateQueries({ queryKey: ["indicator-results"] });
        })
        .catch(() => undefined);
    };
    const offline = () => refresh(false, false).catch(() => undefined);
    const change = () => refresh(false, state.online).catch(() => undefined);
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    window.addEventListener("siscom-offline-change", change);
    const timer = window.setInterval(() => {
      online();
    }, 30_000);
    online();
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
      window.removeEventListener("siscom-offline-change", change);
      window.clearInterval(timer);
    };
  }, [client]);
  return null;
}
